"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { PortfolioApiQuestion } from "@/type/servicesPageTypes";
import TestSectionsSidebar from "../test-subcomponents/TestSectionsSidebar";
import QuestionItem from "../test-subcomponents/QuestionItem";
import NavigationButtons from "../test-subcomponents/NavigationButtons";
import { apiPost } from "@/utils/apiClient";
import { useRouter } from "next/navigation";
import { useLoginModal } from "@/utils/useLoginModal";

type Props = {
  questions: PortfolioApiQuestion[];
};

const INVESTMENT_TYPE_QID = 140;
const SIP_AMOUNT_QID = 141;
const LUMPSUM_AMOUNT_QID = 142;

const SIP_OPTION_ID = 412;
const LUMPSUM_OPTION_ID = 413;
const BOTH_OPTION_ID = 414;

export default function ClientAssessmentTestSection({ questions }: Props) {
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [hasMounted, setHasMounted] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [answers, setAnswers] = useState<{
    [key: number]: {
      question_id: number;
      option_id: number;
      answer: string;
    };
  }>({});

  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const route = useRouter();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // -------- Grouping Questions by Stepper Title ---------
  const groupedSteps = useMemo(() => {
    const map = new Map<
      string,
      {
        title: string;
        questions: PortfolioApiQuestion[];
      }
    >();

    questions.forEach((question) => {
      const title = question.stepper_title?.trim() || "Untitled";

      const existing = map.get(title);

      if (existing) {
        existing.questions.push(question);
      } else {
        map.set(title, {
          title,
          questions: [question],
        });
      }
    });

    return Array.from(map.values()).map((item, index) => ({
      id: index + 1,
      title: item.title,
      questions: item.questions,
    }));
  }, [questions]);

  // -------- Current Step State ---------
  const [currentStep, setCurrentStep] = useState<number>(1);
  const activeStep = groupedSteps.find((step) => step.id === currentStep);

  // -------- Active Question Synchronization ---------
  useEffect(() => {
    if (activeStep?.questions.length) {
      const firstUnanswered = activeStep.questions.find((q) => !answers[q.id]);
      setActiveQuestionId(
        firstUnanswered ? firstUnanswered.id : activeStep.questions[0].id,
      );
    }
  }, [currentStep, activeStep]);

  // -------- Scroll To Question Function ---------
  const scrollToQuestion = (questionId: number) => {
    const container = scrollContainerRef.current;

    const element = questionRefs.current[questionId];

    if (!container || !element) return;

    const containerRect = container.getBoundingClientRect();

    const elementRect = element.getBoundingClientRect();

    const top = elementRect.top - containerRect.top + container.scrollTop;

    container.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  // -------- Handle Select Function ---------
  const handleSelect = (
    questionId: number,
    optionId: number,
    answer: string,
  ) => {
    setAnswers((prev) => {
      const next = {
        ...prev,
        [questionId]: {
          question_id: questionId,
          option_id: optionId,
          answer,
        },
      };

      if (questionId === INVESTMENT_TYPE_QID) {
        if (optionId === SIP_OPTION_ID) {
          delete next[LUMPSUM_AMOUNT_QID];
        }

        if (optionId === LUMPSUM_OPTION_ID) {
          delete next[SIP_AMOUNT_QID];
        }
      }

      return next;
    });

    if (questionId === INVESTMENT_TYPE_QID) {
      setErrors((prev) => {
        const next = { ...prev };

        if (optionId === SIP_OPTION_ID) {
          delete next[LUMPSUM_AMOUNT_QID];
        }

        if (optionId === LUMPSUM_OPTION_ID) {
          delete next[SIP_AMOUNT_QID];
        }

        return next;
      });
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

    setActiveQuestionId(questionId);

    if (activeStep) {
      const currentIndex = activeStep.questions.findIndex(
        (q) => q.id === questionId,
      );

      if (
        currentIndex !== -1 &&
        currentIndex < activeStep.questions.length - 1
      ) {
        const nextQuestionId = activeStep.questions[currentIndex + 1].id;

        setTimeout(() => {
          scrollToQuestion(nextQuestionId);
          setActiveQuestionId(nextQuestionId);
        }, 250);
      }
    }
  };

  // -------- Handle Submit Function ---------
  const handleSubmit = async () => {
    if (!isLoggedIn) {
      open();
      setModalKey("client-assessment-test");
      return;
    }

    setIsSubmitting(true);

    try {
      const finalAnswers = { ...answers };
      if (!finalAnswers[143]) {
        finalAnswers[143] = {
          question_id: 143,
          option_id: 0,
          answer: "0",
        };
      }

      const payload = {
        subject: "Client Assessment 2.o",
        answerList: finalAnswers,
        device: "Web",
        schemeData: null,
      };

      const res = await apiPost("feedback/submitFeedback", payload);

      if (!res.status || res.status != 1)
        throw new Error(res.message || "Failed to submit assessment");

      route.push(`/client-assessment/result`);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------- Validation Function ---------
  const validateCurrentStep = () => {
    if (!activeStep) return true;

    const newErrors: Record<number, string> = {};

    let firstInvalidId: number | null = null;

    activeStep.questions.forEach((question) => {
      const investmentType = answers[INVESTMENT_TYPE_QID]?.option_id;

      const isHidden =
        (question.id === SIP_AMOUNT_QID &&
          investmentType !== SIP_OPTION_ID &&
          investmentType !== BOTH_OPTION_ID) ||
        (question.id === LUMPSUM_AMOUNT_QID &&
          investmentType !== LUMPSUM_OPTION_ID &&
          investmentType !== BOTH_OPTION_ID);

      if (isHidden) return;

      const isRequired = question.required_status === "1";

      const answerData = answers[question.id];

      let isAnswered = false;

      if (question.field_type === "amount") {
        const amount = Number(answerData?.answer || 0);

        if (question.id === SIP_AMOUNT_QID) {
          isAnswered = amount >= 5000;
        } else if (question.id === LUMPSUM_AMOUNT_QID) {
          isAnswered = amount >= 100000;
        } else if (question.id === 143) {
          isAnswered = true;
        } else {
          isAnswered = amount > 0;
        }
      } else {
        isAnswered = answerData?.option_id !== undefined;
      }

      if (isRequired && !isAnswered) {
        if (question.field_type === "amount") {
          newErrors[question.id] =
            question.id === SIP_AMOUNT_QID
              ? "Minimum amount is ₹5,000"
              : question.id === LUMPSUM_AMOUNT_QID
                ? "Minimum amount is ₹1,00,000"
                : "Please enter an amount";
        } else {
          newErrors[question.id] = "Please select an option";
        }

        if (!firstInvalidId) {
          firstInvalidId = question.id;
        }
      }
    });

    setErrors(newErrors);

    if (firstInvalidId) {
      requestAnimationFrame(() => {
        scrollToQuestion(firstInvalidId!);
      });

      return false;
    }

    return true;
  };

  // --------- Next Navigation Functions ---------
  const nextStep = async () => {
    const isValid = validateCurrentStep();

    if (!isValid) return;

    if (currentStep < groupedSteps.length) {
      setCurrentStep((prev) => prev + 1);

      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    } else {
      await handleSubmit();
    }
  };

  // --------- Back Navigation Functions ---------
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);

      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  };

  return (
    <div className="relative grid grid-cols-1 overflow-hidden md:grid-cols-[320px_minmax(0,1fr)] lg:grid-cols-[420px_minmax(0,1fr)] bg-white border sm:border-2 border-[#EAECF0] rounded-4xl sm:rounded-6xl lg:rounded-8xl">
      {/* Sidebar */}
      <TestSectionsSidebar
        groupedSteps={groupedSteps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        scrollContainerRef={scrollContainerRef}
      />

      {/* Content */}
      <div className="relative overflow-hidden p-4 md:p-5 lg:p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            ref={scrollContainerRef}
            initial={
              hasMounted
                ? {
                    opacity: 0,
                    x: 20,
                  }
                : false
            }
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              x: -20,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
            className="relative h-96 sm:h-142 overflow-y-auto scrollbar-none"
          >
            <div className="hidden md:block mb-4 text-sm sm:text-base text-primaryBlack/50">
              {activeStep?.title}
            </div>

            <div className="space-y-9 sm:space-y-14 pb-8">
              {activeStep?.questions
                .filter((question) => {
                  const investmentType =
                    answers[INVESTMENT_TYPE_QID]?.option_id;

                  // SIP Amount
                  if (question.id === SIP_AMOUNT_QID) {
                    return (
                      investmentType === SIP_OPTION_ID ||
                      investmentType === BOTH_OPTION_ID
                    );
                  }

                  // Lumpsum Amount
                  if (question.id === LUMPSUM_AMOUNT_QID) {
                    return (
                      investmentType === LUMPSUM_OPTION_ID ||
                      investmentType === BOTH_OPTION_ID
                    );
                  }

                  return true;
                })
                .map((question) => (
                  <QuestionItem
                    key={question.id}
                    question={question}
                    answers={answers}
                    errors={errors}
                    setErrors={setErrors}
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    handleSelect={handleSelect}
                    isActive={activeQuestionId === question.id}
                    onClick={() => setActiveQuestionId(question.id)}
                    questionRefCallback={(el) => {
                      questionRefs.current[question.id] = el;
                    }}
                  />
                ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <NavigationButtons
          currentStep={currentStep}
          prevStep={prevStep}
          nextStep={nextStep}
          isLastStep={currentStep === groupedSteps.length}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
}
